import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Utility } from '@libs/apis';
import { IAlertFrameworkApiOperation } from '../models/IAlertFrameworkApiOperation';
import {
  IAlertAction,
  IAlertActionCreateRequest,
  IAlertActionUpdateRequest,
  IAlertConfigInstance,
  IAlertConfigInstanceCreateRequest,
  IAlertConfigInstanceListFilters,
  IAlertConfigInstanceUpdateRequest,
  IAlertDefinition,
  IAlertDefinitionCreateRequest,
  IAlertDefinitionUpdateRequest,
  IAlertDictionary,
  IAlertDictionaryCreateRequest,
  IAlertDictionaryErrorMapReplaceRequest,
  IAlertDictionaryUpdateRequest,
  IAlertLifecycleConfig,
  IAlertLifecycleConfigCreateRequest,
  IAlertLifecycleConfigUpdateRequest,
  IAlertStatusUpdateRequest,
  IAlertTemplate,
  IAlertTemplateBody,
  IAlertTemplateCreateRequest,
  IAlertTemplateUpdateRequest,
  IAlertTemplateValidateRequest,
  IAlertTemplateValidateResponse,
  IProductAlertCode,
  IProductAlertCodeCreateRequest,
  IProductAlertCodeUpdateRequest
} from '../models/IAlertFramework';

const promiseFrom = lastValueFrom;

export class AlertFrameworkApis implements IAlertFrameworkApiOperation {
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string,
    private readonly apiV2: string
  ) {}

  public static alertFrameworkApiOperation(
    httpClient: HttpClient,
    apiV1: string,
    apiV2: string
  ): IAlertFrameworkApiOperation {
    return new AlertFrameworkApis(httpClient, apiV1, apiV2);
  }

  public async listProductAlertCodes(
    productId?: string
  ): Promise<IProductAlertCode[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/product-alert-codes`,
      queryParameters: { productId }
    });
    return promiseFrom(this.httpClient.get<IProductAlertCode[]>(url));
  }

  public async getProductAlertCode(id: number): Promise<IProductAlertCode> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/product-alert-codes/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IProductAlertCode>(url));
  }

  public async createProductAlertCode(
    request: IProductAlertCodeCreateRequest
  ): Promise<IProductAlertCode> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/product-alert-codes`
    });
    return promiseFrom(this.httpClient.post<IProductAlertCode>(url, request));
  }

  public async updateProductAlertCode(
    id: number,
    request: IProductAlertCodeUpdateRequest
  ): Promise<IProductAlertCode> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/product-alert-codes/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IProductAlertCode>(url, request));
  }

  public async updateProductAlertCodeStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IProductAlertCode> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/product-alert-codes/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IProductAlertCode>(url, request));
  }

  public async listAlertDictionaries(): Promise<IAlertDictionary[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-dictionaries`
    });
    return promiseFrom(this.httpClient.get<IAlertDictionary[]>(url));
  }

  public async getAlertDictionary(id: number): Promise<IAlertDictionary> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-dictionaries/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertDictionary>(url));
  }

  public async createAlertDictionary(
    request: IAlertDictionaryCreateRequest
  ): Promise<IAlertDictionary> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-dictionaries`
    });
    return promiseFrom(this.httpClient.post<IAlertDictionary>(url, request));
  }

  public async updateAlertDictionary(
    id: number,
    request: IAlertDictionaryUpdateRequest
  ): Promise<IAlertDictionary> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-dictionaries/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertDictionary>(url, request));
  }

  public async replaceAlertDictionaryErrorCodes(
    id: number,
    request: IAlertDictionaryErrorMapReplaceRequest
  ): Promise<IAlertDictionary> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-dictionaries/:id/error-codes`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertDictionary>(url, request));
  }

  public async updateAlertDictionaryStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IAlertDictionary> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-dictionaries/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertDictionary>(url, request));
  }

  public async listAlertDefinitions(
    dictionaryId?: number
  ): Promise<IAlertDefinition[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-definitions`,
      queryParameters: { dictionaryId }
    });
    return promiseFrom(this.httpClient.get<IAlertDefinition[]>(url));
  }

  public async getAlertDefinition(id: number): Promise<IAlertDefinition> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-definitions/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertDefinition>(url));
  }

  public async createAlertDefinition(
    request: IAlertDefinitionCreateRequest
  ): Promise<IAlertDefinition> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-definitions`
    });
    return promiseFrom(this.httpClient.post<IAlertDefinition>(url, request));
  }

  public async updateAlertDefinition(
    id: number,
    request: IAlertDefinitionUpdateRequest
  ): Promise<IAlertDefinition> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-definitions/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertDefinition>(url, request));
  }

  public async updateAlertDefinitionStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IAlertDefinition> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-definitions/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertDefinition>(url, request));
  }

  public async listAlertTemplates(
    dictionaryId?: number
  ): Promise<IAlertTemplate[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates`,
      queryParameters: { dictionaryId }
    });
    return promiseFrom(this.httpClient.get<IAlertTemplate[]>(url));
  }

  public async getAlertTemplate(id: number): Promise<IAlertTemplate> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertTemplate>(url));
  }

  public async createAlertTemplate(
    request: IAlertTemplateCreateRequest
  ): Promise<IAlertTemplate> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates`
    });
    return promiseFrom(this.httpClient.post<IAlertTemplate>(url, request));
  }

  public async updateAlertTemplate(
    id: number,
    request: IAlertTemplateUpdateRequest
  ): Promise<IAlertTemplate> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertTemplate>(url, request));
  }

  public async updateAlertTemplateStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IAlertTemplate> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertTemplate>(url, request));
  }

  public async validateAlertTemplate(
    request: IAlertTemplateValidateRequest
  ): Promise<IAlertTemplateValidateResponse> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates/validate`
    });
    return promiseFrom(
      this.httpClient.post<IAlertTemplateValidateResponse>(url, request)
    );
  }

  public async getAlertTemplateBody(id: number): Promise<IAlertTemplateBody> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-templates/:id/body`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertTemplateBody>(url));
  }

  public async listAlertActions(errorId?: number): Promise<IAlertAction[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-actions`,
      queryParameters: { errorId }
    });
    return promiseFrom(this.httpClient.get<IAlertAction[]>(url));
  }

  public async getAlertAction(id: number): Promise<IAlertAction> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-actions/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertAction>(url));
  }

  public async createAlertAction(
    request: IAlertActionCreateRequest
  ): Promise<IAlertAction> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-actions`
    });
    return promiseFrom(this.httpClient.post<IAlertAction>(url, request));
  }

  public async updateAlertAction(
    id: number,
    request: IAlertActionUpdateRequest
  ): Promise<IAlertAction> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-actions/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertAction>(url, request));
  }

  public async updateAlertActionStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IAlertAction> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-actions/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertAction>(url, request));
  }

  public async listAlertLifecycleConfigs(
    errorId?: number
  ): Promise<IAlertLifecycleConfig[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-lifecycle-configs`,
      queryParameters: { errorId }
    });
    return promiseFrom(this.httpClient.get<IAlertLifecycleConfig[]>(url));
  }

  public async getAlertLifecycleConfig(
    id: number
  ): Promise<IAlertLifecycleConfig> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-lifecycle-configs/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertLifecycleConfig>(url));
  }

  public async createAlertLifecycleConfig(
    request: IAlertLifecycleConfigCreateRequest
  ): Promise<IAlertLifecycleConfig> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-lifecycle-configs`
    });
    return promiseFrom(
      this.httpClient.post<IAlertLifecycleConfig>(url, request)
    );
  }

  public async updateAlertLifecycleConfig(
    id: number,
    request: IAlertLifecycleConfigUpdateRequest
  ): Promise<IAlertLifecycleConfig> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-lifecycle-configs/:id`,
      pathVariables: { id }
    });
    return promiseFrom(
      this.httpClient.put<IAlertLifecycleConfig>(url, request)
    );
  }

  public async updateAlertLifecycleConfigStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IAlertLifecycleConfig> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-lifecycle-configs/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(
      this.httpClient.put<IAlertLifecycleConfig>(url, request)
    );
  }

  public async listAlertConfigInstances(
    filters?: IAlertConfigInstanceListFilters
  ): Promise<IAlertConfigInstance[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-config-instances`,
      queryParameters: {
        dictionaryId: filters?.dictionaryId,
        clientId: filters?.clientId,
        brandId: filters?.brandId
      }
    });
    return promiseFrom(this.httpClient.get<IAlertConfigInstance[]>(url));
  }

  public async getAlertConfigInstance(
    id: number
  ): Promise<IAlertConfigInstance> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-config-instances/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.get<IAlertConfigInstance>(url));
  }

  public async createAlertConfigInstance(
    request: IAlertConfigInstanceCreateRequest
  ): Promise<IAlertConfigInstance> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-config-instances`
    });
    return promiseFrom(
      this.httpClient.post<IAlertConfigInstance>(url, request)
    );
  }

  public async updateAlertConfigInstance(
    id: number,
    request: IAlertConfigInstanceUpdateRequest
  ): Promise<IAlertConfigInstance> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-config-instances/:id`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertConfigInstance>(url, request));
  }

  public async updateAlertConfigInstanceStatus(
    id: number,
    request: IAlertStatusUpdateRequest
  ): Promise<IAlertConfigInstance> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/alert-config-instances/:id/status`,
      pathVariables: { id }
    });
    return promiseFrom(this.httpClient.put<IAlertConfigInstance>(url, request));
  }
}
